import { Icon } from '@iconify/react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import type { RaspadinhaCampaign, RaspadinhaDayWinningSchedule } from '@/types/raspadinha-campaign'
import {
  buildWinningSchedulesCsvFilename,
  countConfiguredWinningTimeSlots,
  countDatesWithWinningTimes,
  countTotalWinnersInSchedules,
  downloadWinningSchedulesCsv,
  ensureDateWinningSchedules,
  listCampaignCalendarDates,
} from '@/lib/raspadinha-campaigns'
import editStyles from '../CampanhasPage/CampanhaEditPage.module.css'
import scheduleStyles from './RaspadinhaWeeklyWinningSchedules.module.css'
import { RaspadinhaDateSchedulesEditor } from './RaspadinhaDateSchedulesEditor'
import { RaspadinhaGeneralTimeSlotsGenerator } from './RaspadinhaGeneralTimeSlotsGenerator'
import { RaspadinhaContemplatedPrizeUsageSummary } from './RaspadinhaContemplatedPrizeUsageSummary'

function toDateTimeLocal(value: string | null): string {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`
}

function toIsoDateTime(value: string): string | null {
  if (!value.trim()) return null
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return null
  return date.toISOString()
}

type RaspadinhaValoresSectionProps = {
  form: RaspadinhaCampaign
  onFormChange: (next: RaspadinhaCampaign) => void
  onPendingSchedulesChange?: (hasPending: boolean) => void
  onPendingPeriodChange?: (hasPending: boolean) => void
}

export function RaspadinhaValoresSection({
  form,
  onFormChange,
  onPendingSchedulesChange,
  onPendingPeriodChange,
}: RaspadinhaValoresSectionProps) {
  const [draftStartsAt, setDraftStartsAt] = useState(() => toDateTimeLocal(form.starts_at))
  const [draftEndsAt, setDraftEndsAt] = useState(() => toDateTimeLocal(form.ends_at))
  const [periodApplyError, setPeriodApplyError] = useState<string | null>(null)
  const [hasPendingDayGeneratedSchedules, setHasPendingDayGeneratedSchedules] = useState(false)
  const [hasPendingGeneralGeneratedSchedules, setHasPendingGeneralGeneratedSchedules] =
    useState(false)

  useEffect(() => {
    setDraftStartsAt(toDateTimeLocal(form.starts_at))
    setDraftEndsAt(toDateTimeLocal(form.ends_at))
    setPeriodApplyError(null)
  }, [form.starts_at, form.ends_at])

  const appliedStartsLocal = toDateTimeLocal(form.starts_at)
  const appliedEndsLocal = toDateTimeLocal(form.ends_at)

  const hasPendingPeriodChange =
    draftStartsAt !== appliedStartsLocal || draftEndsAt !== appliedEndsLocal

  useEffect(() => {
    onPendingPeriodChange?.(hasPendingPeriodChange)
  }, [hasPendingPeriodChange, onPendingPeriodChange])

  useEffect(() => {
    onPendingSchedulesChange?.(
      hasPendingDayGeneratedSchedules || hasPendingGeneralGeneratedSchedules
    )
  }, [
    hasPendingDayGeneratedSchedules,
    hasPendingGeneralGeneratedSchedules,
    onPendingSchedulesChange,
  ])

  const resolvedSchedules = ensureDateWinningSchedules(
    form.winning_date_schedules,
    form.starts_at,
    form.ends_at
  )

  const configuredTimes = countConfiguredWinningTimeSlots(resolvedSchedules)
  const datesWithTimes = countDatesWithWinningTimes(resolvedSchedules)
  const totalWinners = countTotalWinnersInSchedules(resolvedSchedules)
  const totalDefinedSlots = resolvedSchedules.reduce(
    (total, schedule) => total + schedule.time_slots.length,
    0
  )

  const appliedDayCount = useMemo(
    () => listCampaignCalendarDates(form.starts_at, form.ends_at).length,
    [form.starts_at, form.ends_at]
  )

  const draftDayCount = useMemo(() => {
    const nextStartsAt = toIsoDateTime(draftStartsAt)
    const nextEndsAt = toIsoDateTime(draftEndsAt)
    return listCampaignCalendarDates(nextStartsAt, nextEndsAt).length
  }, [draftStartsAt, draftEndsAt])

  const handleExportSchedulesCsv = () => {
    downloadWinningSchedulesCsv(
      resolvedSchedules,
      buildWinningSchedulesCsvFilename(form.slug),
      { contemplatedPrizes: form.contemplated_prize_images }
    )
  }

  const handleApplyPeriod = useCallback(() => {
    setPeriodApplyError(null)

    const nextStartsAt = toIsoDateTime(draftStartsAt)
    const nextEndsAt = toIsoDateTime(draftEndsAt)

    if (!draftStartsAt.trim() || !draftEndsAt.trim()) {
      setPeriodApplyError('Informe a data e hora de início e de encerramento.')
      return
    }

    if (!nextStartsAt || !nextEndsAt) {
      setPeriodApplyError('As datas informadas são inválidas.')
      return
    }

    const startMs = new Date(nextStartsAt).getTime()
    const endMs = new Date(nextEndsAt).getTime()

    if (startMs > endMs) {
      setPeriodApplyError('A data de início deve ser anterior à data de encerramento.')
      return
    }

    const winning_date_schedules: RaspadinhaDayWinningSchedule[] = ensureDateWinningSchedules(
      form.winning_date_schedules,
      nextStartsAt,
      nextEndsAt
    )

    onFormChange({
      ...form,
      starts_at: nextStartsAt,
      ends_at: nextEndsAt,
      winning_date_schedules,
    })
  }, [draftEndsAt, draftStartsAt, form, onFormChange])

  return (
    <section className={editStyles.editSection} aria-labelledby="raspadinha-contemplacoes">
      <h2 id="raspadinha-contemplacoes" className={editStyles.editSectionTitle}>
        <Icon className={editStyles.editSectionTitleIcon} icon="mdi:calendar-clock-outline" />
        Contemplações
      </h2>
      <div className={editStyles.editSectionBody}>
        <div className={`${editStyles.editSubsection} ${scheduleStyles.contemplacoesPeriodSubsection}`}>
          <h3 className={editStyles.editSubsectionTitle}>Período da campanha</h3>
          <p className={editStyles.editLabelHint}>
            Ajuste as datas abaixo e clique em <strong>Alterar período</strong> para atualizar a
            lista de dias disponíveis para contemplações.
          </p>
          <div className={editStyles.editGrid2}>
            <div className={editStyles.editField}>
              <label className={editStyles.editLabel} htmlFor="raspadinha-starts">
                Data e hora de início
              </label>
              <input
                id="raspadinha-starts"
                type="datetime-local"
                className={editStyles.editInput}
                value={draftStartsAt}
                onChange={(event) => {
                  setPeriodApplyError(null)
                  setDraftStartsAt(event.target.value)
                }}
              />
            </div>
            <div className={editStyles.editField}>
              <label className={editStyles.editLabel} htmlFor="raspadinha-ends">
                Data e hora de encerramento
              </label>
              <input
                id="raspadinha-ends"
                type="datetime-local"
                className={editStyles.editInput}
                value={draftEndsAt}
                onChange={(event) => {
                  setPeriodApplyError(null)
                  setDraftEndsAt(event.target.value)
                }}
              />
            </div>
          </div>

          <div className={scheduleStyles.periodActions}>
            <button
              type="button"
              className={`${scheduleStyles.toolbarButton} ${scheduleStyles.toolbarButtonPrimary}`}
              onClick={handleApplyPeriod}
              disabled={!hasPendingPeriodChange}
              title={
                hasPendingPeriodChange
                  ? 'Aplicar o novo período e atualizar os dias da campanha'
                  : 'Nenhuma alteração pendente no período'
              }
            >
              <Icon icon="mdi:calendar-sync-outline" aria-hidden />
              Alterar período
            </button>

            {hasPendingPeriodChange ? (
              <p className={scheduleStyles.periodPendingHint} role="status">
                Período alterado: a lista abaixo ainda mostra {appliedDayCount} dia(s). Ao
                confirmar, serão exibidos {draftDayCount} dia(s).
              </p>
            ) : (
              <p className={scheduleStyles.periodAppliedHint} role="status">
                Período aplicado com {appliedDayCount} dia(s) na campanha.
              </p>
            )}
          </div>

          {periodApplyError ? (
            <p className={scheduleStyles.periodError} role="alert">
              {periodApplyError}
            </p>
          ) : null}
        </div>

        <hr className={scheduleStyles.sectionDivider} aria-hidden />

        <div className={scheduleStyles.contemplacoesToolsGroup}>
          <RaspadinhaGeneralTimeSlotsGenerator
            schedules={form.winning_date_schedules}
            contemplatedPrizes={form.contemplated_prize_images}
            startsAt={form.starts_at}
            endsAt={form.ends_at}
            disabled={hasPendingPeriodChange}
            disabledReason={
              hasPendingPeriodChange
                ? 'Confirme o novo período com Alterar período antes de usar o gerador geral.'
                : null
            }
            onSchedulesChange={(winning_date_schedules) =>
              onFormChange({ ...form, winning_date_schedules })
            }
            onPendingChange={setHasPendingGeneralGeneratedSchedules}
          />

          <RaspadinhaContemplatedPrizeUsageSummary
            schedules={form.winning_date_schedules}
            contemplatedPrizes={form.contemplated_prize_images}
            startsAt={form.starts_at}
            endsAt={form.ends_at}
          />
        </div>

        <hr className={scheduleStyles.sectionDivider} aria-hidden />

        <div className={`${editStyles.editSubsection} ${scheduleStyles.contemplacoesSchedulesSubsection}`}>
          <div className={scheduleStyles.schedulesSubsectionHeader}>
            <h3
              className={`${editStyles.editSubsectionTitle} ${scheduleStyles.schedulesSubsectionTitle}`}
            >
              Raspadinhas premiadas por dia da campanha
            </h3>
            <button
              type="button"
              className={scheduleStyles.exportSchedulesButton}
              onClick={handleExportSchedulesCsv}
              disabled={totalDefinedSlots === 0}
              title={
                totalDefinedSlots === 0
                  ? 'Cadastre horários antes de exportar'
                  : 'Baixar horários de contemplação em CSV'
              }
            >
              <Icon icon="mdi:file-export-outline" aria-hidden />
              Exportar horários (CSV)
            </button>
          </div>
          <p className={editStyles.editLabelHint}>
            Clique no dia ou na linha do horário para expandir. Os ganhadores são exibidos em tabela
            de leitura; use os botões de adicionar ou editar para alterar os dados.
          </p>
          <p className={`${editStyles.editLabelHint} ${scheduleStyles.summaryHint}`}>
            {configuredTimes} horário(s) · {totalWinners} ganhador(es) · {datesWithTimes} data(s)
            com horários configurados.
          </p>

          <RaspadinhaDateSchedulesEditor
            schedules={form.winning_date_schedules}
            contemplatedPrizes={form.contemplated_prize_images}
            startsAt={form.starts_at}
            endsAt={form.ends_at}
            onSchedulesChange={(winning_date_schedules) =>
              onFormChange({ ...form, winning_date_schedules })
            }
            onPendingSchedulesChange={setHasPendingDayGeneratedSchedules}
          />
        </div>
      </div>
    </section>
  )
}
